import { sceneConstants } from '../actions/constants'
import { arr3eq } from '../helpers/arr3eq'
import { normalizeGraphicsQuality } from '../utils/sceneGraphicsQuality'

const LOCAL_SCENE_SETTINGS = Object.freeze({
    sceneColor: 'ao_sceneColor',
    gridVisible: 'ao_gridVisible',
    gridColor: 'ao_gridColor',
    floorVisible: 'ao_floorVisible',
    floorMode: 'ao_floorMode',
    floorColor: 'ao_floorColor',
    sunAngle: 'ao_sunAngle',
    sunVertical: 'ao_sunVertical',
    sunIntensity: 'ao_sunIntensity',
    shadowsEnabled: 'ao_shadowsEnabled',
    graphicsQuality: 'ao_graphicsQuality',
})

const LOCAL_SCENE_SETTING_NAMES = Object.freeze(Object.keys(LOCAL_SCENE_SETTINGS))
const FLOOR_MODES = Object.freeze(['color', 'alotb', 'grass'])

const clampLocalNumber = (value, min, max, fallback) => {
    const number = Number(value)

    if (!Number.isFinite(number)) return fallback

    return Math.min(max, Math.max(min, number))
}

const readLocalStorageValue = (key) => {
    if (typeof window === 'undefined' || !key) return null

    try {
        return window.localStorage.getItem(key)
    } catch (_error) {
        return null
    }
}

const writeLocalStorageValue = (key, value) => {
    if (typeof window === 'undefined' || !key) return

    try {
        window.localStorage.setItem(key, String(value))
    } catch (_error) {
        // Сцена продолжит работать, даже если браузер запретил LocalStorage.
    }
}

const normalizeLocalSceneSetting = (name, value, fallback) => {
    if (name === 'gridVisible' || name === 'floorVisible' || name === 'shadowsEnabled') {
        if (value === true || value === 'true') return true
        if (value === false || value === 'false') return false
        return !!fallback
    }

    if (name === 'sunAngle') {
        const safeFallback = Number.isFinite(Number(fallback)) ? Number(fallback) : 135
        return clampLocalNumber(value, 0, 360, safeFallback)
    }

    if (name === 'sunVertical') {
        const safeFallback = Number.isFinite(Number(fallback)) ? Number(fallback) : 45
        return clampLocalNumber(value, 5, 85, safeFallback)
    }

    if (name === 'sunIntensity') {
        const safeFallback = Number.isFinite(Number(fallback)) ? Number(fallback) : 1.5
        return clampLocalNumber(value, 0, 10, safeFallback)
    }

    if (name === 'floorMode') {
        const clean = String(value || '').trim().toLowerCase()

        return FLOOR_MODES.includes(clean) ? clean : (FLOOR_MODES.includes(fallback) ? fallback : 'alotb')
    }

    if (name === 'graphicsQuality') {
        return normalizeGraphicsQuality(value, fallback)
    }

    if (name === 'sceneColor' || name === 'gridColor' || name === 'floorColor') {
        const clean = typeof value === 'string' ? value.trim() : ''

        return clean || fallback
    }

    return value ?? fallback
}

const readLocalSceneSetting = (name, fallback) => {
    const key = LOCAL_SCENE_SETTINGS[name]

    if (!key) return { found: false, value: fallback }

    const raw = readLocalStorageValue(key)

    if (raw === null) return { found: false, value: fallback }

    return {
        found: true,
        value: normalizeLocalSceneSetting(name, raw, fallback),
    }
}

const persistLocalSceneSetting = (name, value, fallback) => {
    const normalized = normalizeLocalSceneSetting(name, value, fallback)
    const key = LOCAL_SCENE_SETTINGS[name]

    if (key) writeLocalStorageValue(key, normalized)

    return normalized
}

const getInitialSettings = () => {
    const isMobile = typeof window !== 'undefined' && /Mobi|Android/i.test(window.navigator.userAgent)
    const defaults = {
        sceneColor: '#808080',
        gridVisible: true,
        gridColor: '#888888',
        floorVisible: true,
        floorMode: 'alotb',
        floorColor: '#FAEBD7',
        sunAngle: 135,
        sunVertical: 45,
        sunIntensity: 1.5,
        shadowsEnabled: !isMobile,
        graphicsQuality: isMobile ? 'low' : 'high',
    }

    return LOCAL_SCENE_SETTING_NAMES.reduce((settings, name) => {
        settings[name] = readLocalSceneSetting(name, defaults[name]).value
        return settings
    }, {})
}

const initialSettings = getInitialSettings()

const pickPersistentSceneSettings = (state = {}) => (
    LOCAL_SCENE_SETTING_NAMES.reduce((settings, name) => {
        settings[name] = normalizeLocalSceneSetting(
            name,
            state[name],
            initialSettings[name]
        )
        return settings
    }, {})
)

const initialState = {
    floorVisible: initialSettings.floorVisible,
    floorMode: initialSettings.floorMode,
    floorColor: initialSettings.floorColor,
    floorRepeat: 6,
    floorSize: 10,
    floorText: 'ALOTB.RU',
    showTextDimensions: true,
    moveMode: false,
    moveStep: 0.1,
    isCtrlPressed: false,
    lineColor: '#000000',
    showPivot: false,
    pivotRequest: null,
    pivotPresetById: {},
    transformMode: null,
    textureEditMode: false,
    multiSelectMode: false,
    modifierPreviews: { array: null, arraysBySourceId: {}, activeArraySourceId: null },
    selectedModelPaths: [],
    selectionGroups: {},
    activeSelectionGroupId: null,
    controlPanelActions: [
        { id: 'delete', icon: '🔨', tooltip: 'Удалить', disabled: true },
        { id: 'moveXPlus', icon: '➡️', tooltip: 'X+', disabled: true },
        { id: 'moveXMinus', icon: '⬅️', tooltip: 'X-', disabled: true },
        { id: 'moveYPlus', icon: '⬆️', tooltip: 'Y+', disabled: true },
        { id: 'moveYMinus', icon: '⬇️', tooltip: 'Y-', disabled: true },
        { id: 'moveZPlus', icon: '🔼', tooltip: 'Z+', disabled: true },
        { id: 'moveZMinus', icon: '🔽', tooltip: 'Z-', disabled: true },
        { id: 'rotatePlus', icon: '🔄', tooltip: 'Поворот+', disabled: true },
        { id: 'rotateMinus', icon: '🔃', tooltip: 'Поворот-', disabled: true },
        { id: 'mirrorX', icon: '🪞', tooltip: 'Зеркалить X', disabled: true },
        { id: 'mirrorZ', icon: '🪞', tooltip: 'Зеркалить Z', disabled: true },
        { id: 'groupLink', icon: '🔗', tooltip: 'Связать/выделить группу', disabled: true },
        { id: 'multiSelect', icon: '✅', tooltip: 'Мультивыделение (для телефона)', disabled: false },
        { id: 'screenshot', icon: '📸', tooltip: 'Скрин сцены', disabled: false },
    ],
    productsMetaById: {},
    customObjects: {},
    selectedModel: null,
    hiddenModels: {},
    solidObjects: {},
    monumentAssemblies: {},
    monumentDecorations: {},
    monumentDecorationAssets: {},
    modelPartsMeta: {},
    positions: {},
    rotations: {},
    scales: {},
    pivotPoints: {},
    modelPaths: [],
    dimensions: {},
    selectedModels: {
        fence: null, vase: null, bench: null, table: null, floor: null, concrete: null, gravestones: [],
    },
    colors: {
        fence: '#808080', bench: '#808080', table: '#808080', vase: '#808080', floor: '#808080', concrete: '#808080',
        instanceByPath: {},
        byModelPath: {},
        gravestones: [
            { material: 'Габбро-диабаз', polish: '1 сторона' },
            { material: 'Габбро-диабаз', polish: '4 сторона' },
            { material: 'Дымовский (Балтийский)', polish: '1 сторона' },
            { material: 'Дымовский (Балтийский)', polish: '4 сторона' },
            { material: 'Капустинский', polish: '1 сторона' },
            { material: 'Капустинский', polish: '4 сторона' },
        ],
    },
    monumentPolished: false,
    sceneColor: initialSettings.sceneColor,
    sunAngle: initialSettings.sunAngle,
    sunVertical: initialSettings.sunVertical,
    sunIntensity: initialSettings.sunIntensity,
    shadowsEnabled: initialSettings.shadowsEnabled,
    graphicsQuality: initialSettings.graphicsQuality,
    gridVisible: initialSettings.gridVisible,
    gridColor: initialSettings.gridColor,
    gridPosition: [0, -0.49, 0],
    minZ: 0,
    processedPhoto: null,
    headCenter: { x: 0, y: 0 },
    showDimensionLines: true,
    lineThickness: 1,
    fontSize: 14,
    cameraState: {
        azimuthalAngle: 0, polarAngle: Math.PI / 2, position: [5, 5, 5],
    },
    history: { past: [], future: [], limit: 30, lastHash: '' },
}

const getBaseId = (id) => typeof id === 'string' && id.includes('__') ? id.split('__')[0] : id

const getDeterministicPaths = (selectedModels, customObjects) => {
    const paths = []
    const counts = {}

    const addId = (rawId) => {
        if (!rawId) return
        const baseId = getBaseId(rawId)
        if (!counts[baseId]) {
            counts[baseId] = 1
            paths.push(baseId)
        } else {
            paths.push(`${baseId}__${counts[baseId]}`)
            counts[baseId]++
        }
    }

    if (selectedModels.karkas) addId(selectedModels.karkas)
    if (selectedModels.monumentLeft) addId(selectedModels.monumentLeft)

    const keys = Object.keys(selectedModels || {}).filter(k => k !== 'karkas' && k !== 'monumentLeft' && k !== 'gravestones').sort()
    keys.forEach(k => {
        const v = selectedModels[k]
        if (Array.isArray(v)) v.forEach(addId)
        else if (typeof v === 'string') addId(v)
    })

    const customKeys = Object.keys(customObjects || {}).sort()
    customKeys.forEach(k => addId(k))

    return paths
}

const ensureDefaultsForId = (state, id, baseDimensions) => {
    const nextPositions = { ...(state.positions || {}) }
    const nextRotations = { ...(state.rotations || {}) }
    const nextScales = { ...(state.scales || {}) }
    const nextDimensions = { ...(state.dimensions || {}) }

    if (!nextPositions[id]) nextPositions[id] = [0, 0, 0]
    if (!nextRotations[id]) nextRotations[id] = [0, 0, 0]
    if (!nextScales[id]) nextScales[id] = [1, 1, 1]

    if (baseDimensions && typeof baseDimensions === 'object') {
        nextDimensions[id] = baseDimensions
    }
    return { nextPositions, nextRotations, nextScales, nextDimensions }
}

const clampNum = (n, min, max) => Math.min(max, Math.max(min, n))
const toArr3 = (v, fallback = [1, 1, 1]) => (Array.isArray(v) ? v : fallback)
const abs3 = (arr) => [0, 1, 2].map((i) => Math.max(0.000001, Math.abs(Number(arr?.[i]) || 1)))

const CUSTOM_MATERIAL_SOURCE_TYPES = new Set(['stone', 'color', 'upload'])

const DEFAULT_PACK = {
    sourceType: 'color',
    materialKey: '',
    materialName: '',
    color: '#ffffff',
    url: null,
    repeat: [1, 1],
    offset: [0, 0],
    rotation: 0,
    roughness: 0.72,
    metalness: 0,
    adminCatalogSemantics: false,
    densityKgM3: undefined,
    catalogRevision: 0,
}

const normalizeCustomMaterialKey = (value) => {
    const key = String(value || '').trim().toLowerCase()
    return key === 'kapustinsky' ? 'vinga' : key
}

const hasOwnPackValue = (value, key) => (
    !!value
    && typeof value === 'object'
    && Object.prototype.hasOwnProperty.call(value, key)
)

const inferCustomMaterialSourceType = (source, fallback = DEFAULT_PACK) => {
    const src = source && typeof source === 'object' ? source : {}
    const fb = fallback && typeof fallback === 'object' ? fallback : DEFAULT_PACK
    const explicit = String(src.sourceType || '').trim().toLowerCase()

    if (CUSTOM_MATERIAL_SOURCE_TYPES.has(explicit)) return explicit

    const directMaterialKey = normalizeCustomMaterialKey(src.materialKey)
    if (directMaterialKey || src.adminCatalogSemantics === true) return 'stone'

    if (hasOwnPackValue(src, 'url')) {
        if (typeof src.url === 'string' && src.url.trim()) return 'upload'
        if (src.url === null && hasOwnPackValue(src, 'color')) return 'color'
    }

    if (hasOwnPackValue(src, 'color') && !directMaterialKey) return 'color'

    const fallbackExplicit = String(fb.sourceType || '').trim().toLowerCase()
    if (CUSTOM_MATERIAL_SOURCE_TYPES.has(fallbackExplicit)) return fallbackExplicit

    if (normalizeCustomMaterialKey(fb.materialKey) || fb.adminCatalogSemantics === true) return 'stone'
    if (typeof fb.url === 'string' && fb.url.trim()) return 'upload'

    return 'color'
}

const normalizePack = (p, fallback) => {
    const x = p && typeof p === 'object' ? p : {}
    const fb = fallback && typeof fallback === 'object' ? fallback : DEFAULT_PACK
    const sourceType = inferCustomMaterialSourceType(x, fb)
    const merged = { ...fb, ...x }
    const repeat = Array.isArray(x.repeat)
        ? x.repeat.slice(0, 2)
        : (Array.isArray(fb.repeat) ? fb.repeat.slice(0, 2) : [1, 1])
    const offset = Array.isArray(x.offset)
        ? x.offset.slice(0, 2)
        : (Array.isArray(fb.offset) ? fb.offset.slice(0, 2) : [0, 0])
    const rotation = Number.isFinite(Number(x.rotation))
        ? Number(x.rotation)
        : (Number.isFinite(Number(fb.rotation)) ? Number(fb.rotation) : 0)
    const roughness = Number.isFinite(Number(merged.roughness))
        ? clampNum(Number(merged.roughness), 0, 1)
        : 0.72
    const metalness = Number.isFinite(Number(merged.metalness))
        ? clampNum(Number(merged.metalness), 0, 1)
        : 0
    const materialKey = sourceType === 'stone'
        ? normalizeCustomMaterialKey(merged.materialKey)
        : ''
    const materialName = sourceType === 'stone'
        ? String(merged.materialName || '')
        : ''
    const color = String(merged.color || '#ffffff')
    let url = null

    if (hasOwnPackValue(x, 'url') && (typeof x.url === 'string' || x.url === null)) {
        url = x.url
    } else if (sourceType !== 'color' && (typeof fb.url === 'string' || fb.url === null)) {
        url = fb.url
    }

    return {
        ...merged,
        sourceType,
        materialKey,
        materialName,
        color,
        url: sourceType === 'color' ? null : url,
        repeat,
        offset,
        rotation,
        roughness,
        metalness,
        adminCatalogSemantics: sourceType === 'stone' && (
            merged.adminCatalogSemantics === true || !!materialKey
        ),
        densityKgM3: Number(merged.densityKgM3 || 0) || undefined,
        catalogRevision: Number(merged.catalogRevision || 0) || 0,
    }
}

const FACE_KEYS = ['front', 'back', 'right', 'left', 'top', 'bottom']

const isUploadMaterialPack = (pack, fallback = DEFAULT_PACK) => (
    inferCustomMaterialSourceType(pack, fallback) === 'upload'
)

const applyAutoRepeatToCustomObject = (obj, scaleArr) => {
    if (!obj || typeof obj !== 'object') return obj

    const tRaw = obj.texture && typeof obj.texture === 'object' ? obj.texture : null
    if (!tRaw || !tRaw.lockToSize) return obj

    const density = clampNum(Number(tRaw.density) || 1, 0.01, 100)
    const uiMode = tRaw.uiMode === 'faces' ? 'faces' : 'all'
    const allPack = normalizePack(tRaw.all, DEFAULT_PACK)
    const facesSrc = (uiMode === 'faces' && tRaw.faces && typeof tRaw.faces === 'object')
        ? tRaw.faces
        : {}

    /*
     * lockToSize / density — это только инструмент пользовательской картинки.
     * Каталожный камень обязан сохранять materialKey и канонический texturePreset
     * из ADMIN, а сплошной цвет вообще не должен проходить через UV-density.
     */
    const hasUploadTexture = uiMode === 'faces'
        ? FACE_KEYS.some((key) => isUploadMaterialPack(facesSrc[key], allPack))
        : isUploadMaterialPack(allPack, DEFAULT_PACK)

    if (!hasUploadTexture) return obj

    const base = obj.baseDimensions && typeof obj.baseDimensions === 'object'
        ? obj.baseDimensions
        : { x: 1, y: 1, z: 1 }
    const sAbs = abs3(toArr3(scaleArr, [1, 1, 1]))
    const dx = Math.max(0.000001, (Number(base.x) || 1) * sAbs[0])
    const dy = Math.max(0.000001, (Number(base.y) || 1) * sAbs[1])
    const dz = Math.max(0.000001, (Number(base.z) || 1) * sAbs[2])

    const uvMap = {
        front: [dx, dy],
        back: [dx, dy],
        right: [dz, dy],
        left: [dz, dy],
        top: [dx, dz],
        bottom: [dx, dz],
    }

    const nextAll = isUploadMaterialPack(allPack, DEFAULT_PACK)
        ? { ...allPack, repeat: [dx * density, dy * density] }
        : allPack
    const nextFaces = {}

    FACE_KEYS.forEach((key) => {
        const previousRaw = facesSrc[key]
        const previousPack = normalizePack(previousRaw, allPack)
        const uv = uvMap[key] || [1, 1]

        nextFaces[key] = isUploadMaterialPack(previousPack, allPack)
            ? { ...previousPack, repeat: [uv[0] * density, uv[1] * density] }
            : previousPack
    })

    return {
        ...obj,
        texture: {
            ...tRaw,
            uiMode,
            mode: 'faces',
            all: nextAll,
            faces: nextFaces,
        },
    }
}

const HISTORY_JUMP_TYPE = 'SCENE_HISTORY_JUMP'
const ARRAY_PREVIEW_SET_TYPE = 'SET_SCENE_ARRAY_MODIFIER_PREVIEW'
const ARRAY_PREVIEW_CLEAR_TYPE = 'CLEAR_SCENE_ARRAY_MODIFIER_PREVIEW'
const REPLACE_SCENE_INSTANCE_STATE_TYPE = 'REPLACE_SCENE_INSTANCE_STATE'
const HISTORY_DEFAULT_LIMIT = 30

const EMPTY_MODIFIER_PREVIEWS = {
    array: null,
    arraysBySourceId: {},
    activeArraySourceId: null,
}

const normalizeArrayPreviewItem = (preview) => {
    if (!preview?.enabled || !preview?.sourceId) return null

    return {
        enabled: true,
        sourceId: preview.sourceId,
        count: Math.min(999, Math.max(2, parseInt(preview.count, 10) || 5)),
        relative: preview.relative !== false,
        offset: {
            x: Number(preview.offset?.x) || 0,
            y: Number(preview.offset?.y) || 0,
            z: Number(preview.offset?.z) || 0,
        },
        relativeSize: {
            x: Math.max(0.000001, Number(preview.relativeSize?.x) || 1),
            y: Math.max(0.000001, Number(preview.relativeSize?.y) || 1),
            z: Math.max(0.000001, Number(preview.relativeSize?.z) || 1),
        },
    }
}

const normalizeModifierPreviewsState = (value = {}) => {
    const arraysBySourceId = {}
    const sourceMap = value?.arraysBySourceId && typeof value.arraysBySourceId === 'object'
        ? value.arraysBySourceId
        : {}

    Object.keys(sourceMap).forEach((sourceId) => {
        const normalized = normalizeArrayPreviewItem(sourceMap[sourceId])

        if (normalized?.sourceId) {
            arraysBySourceId[normalized.sourceId] = normalized
        }
    })

    const legacy = normalizeArrayPreviewItem(value?.array)

    if (legacy?.sourceId) {
        arraysBySourceId[legacy.sourceId] = legacy
    }

    const ids = Object.keys(arraysBySourceId)
    const activeArraySourceId = (value?.activeArraySourceId && arraysBySourceId[value.activeArraySourceId])
        ? value.activeArraySourceId
        : (legacy?.sourceId && arraysBySourceId[legacy.sourceId])
            ? legacy.sourceId
            : (ids[0] || null)

    return {
        array: activeArraySourceId ? arraysBySourceId[activeArraySourceId] : null,
        arraysBySourceId,
        activeArraySourceId,
    }
}

const stripHistorySnapshotMeta = (snapshot = {}) => {
    const {
        history,
        historyLabel,
        historyAt,
        historyId,
        historyKind,
        historyPayload,
        historyHash,
        viewCheckpoint,
        ...clean
    } = snapshot || {}

    return clean
}

const hashSerializedSnapshot = (serialized = '') => {
    let hashA = 2166136261
    let hashB = 5381

    for (let index = 0; index < serialized.length; index += 1) {
        const code = serialized.charCodeAt(index)
        hashA ^= code
        hashA = Math.imul(hashA, 16777619)
        hashB = Math.imul(hashB, 33) ^ code
    }

    return `${(hashA >>> 0).toString(36)}:${(hashB >>> 0).toString(36)}:${serialized.length}`
}

const getSnapshotHash = (snapshot = {}) => (
    hashSerializedSnapshot(JSON.stringify(stripHistorySnapshotMeta(snapshot)))
)

const makeHistoryEntry = (snapshot = {}, index = 0) => {
    const clean = stripHistorySnapshotMeta(snapshot)
    const label = snapshot?.historyLabel || `Действие ${index + 1}`
    const at = Number(snapshot?.historyAt) || Date.now()
    const id = snapshot?.historyId || `${at}-${index}`

    return {
        ...clean,
        modifierPreviews: normalizeModifierPreviewsState(clean.modifierPreviews || EMPTY_MODIFIER_PREVIEWS),
        historyLabel: label,
        historyKind: snapshot?.historyKind || null,
        historyPayload: snapshot?.historyPayload || null,
        historyAt: at,
        historyId: id,
    }
}

const commitHistoryForNextState = (nextState, meta = {}) => {
    const hist = nextState.history || { past: [], future: [], limit: HISTORY_DEFAULT_LIMIT, lastHash: '' }
    const rawPast = Array.isArray(hist.past) ? hist.past : []
    const limit = Math.max(1, Number(hist.limit) || HISTORY_DEFAULT_LIMIT)
    const entry = makeHistoryEntry({
        ...nextState,
        historyLabel: meta.historyLabel || `Действие ${rawPast.length + 1}`,
        historyKind: meta.historyKind || null,
        historyPayload: meta.historyPayload || null,
        historyAt: meta.historyAt || Date.now(),
    }, rawPast.length)
    const hash = getSnapshotHash(entry)

    if (hash === hist.lastHash) {
        return {
            ...nextState,
            history: {
                ...hist,
                limit,
                lastHash: hash,
            },
        }
    }

    const past = [...rawPast, entry]
    const trimmed = past.length > limit ? past.slice(past.length - limit) : past

    return {
        ...nextState,
        history: {
            ...hist,
            limit,
            past: trimmed,
            future: [],
            lastHash: hash,
        },
    }
}

const applyHistoryEntry = (state, entry, history) => {
    const checkpoint = (
        entry?.historyKind === 'sceneScreenshotCheckpoint' &&
        entry?.historyPayload?.sceneCheckpoint &&
        typeof entry.historyPayload.sceneCheckpoint === 'object'
    )
        ? entry.historyPayload.sceneCheckpoint
        : entry
    const clean = stripHistorySnapshotMeta(checkpoint)
    const hasModifierPreviews = Object.prototype.hasOwnProperty.call(clean, 'modifierPreviews')

    return {
        ...state,
        ...clean,
        modifierPreviews: hasModifierPreviews
            ? normalizeModifierPreviewsState(clean.modifierPreviews || EMPTY_MODIFIER_PREVIEWS)
            : normalizeModifierPreviewsState(state.modifierPreviews || EMPTY_MODIFIER_PREVIEWS),
        history,
    }
}

const isArrayPreviewSetHistoryEntry = (entry = {}) => {
    return entry?.historyKind === 'arrayPreviewSet' || entry?.historyLabel === 'Массив: предпросмотр'
}

const isArrayPreviewClearHistoryEntry = (entry = {}) => {
    return entry?.historyKind === 'arrayPreviewClear' || entry?.historyLabel === 'Массив: отмена'
}

const getPreviewSourceIdFromEntry = (entry = {}) => {
    const explicit = entry?.historyPayload?.sourceId
    if (explicit) return explicit

    const previews = normalizeModifierPreviewsState(entry?.modifierPreviews || EMPTY_MODIFIER_PREVIEWS)

    if (previews.activeArraySourceId) return previews.activeArraySourceId
    if (previews.array?.sourceId) return previews.array.sourceId

    const ids = Object.keys(previews.arraysBySourceId || {})

    return ids.length ? ids[ids.length - 1] : null
}

const buildModifierPreviewsFromMap = (arraysBySourceId = {}, preferredActiveId = null) => {
    const normalized = normalizeModifierPreviewsState({
        arraysBySourceId,
        activeArraySourceId: preferredActiveId,
    })

    return normalized
}

const removeArrayPreviewSourceFromState = (state, sourceId = null) => {
    const prev = normalizeModifierPreviewsState(state.modifierPreviews || EMPTY_MODIFIER_PREVIEWS)
    const ids = Object.keys(prev.arraysBySourceId || {})
    const removeSourceId = sourceId || prev.activeArraySourceId || (ids.length ? ids[ids.length - 1] : null)

    if (!removeSourceId) {
        return {
            ...state,
            modifierPreviews: prev,
        }
    }

    const arraysBySourceId = { ...(prev.arraysBySourceId || {}) }
    delete arraysBySourceId[removeSourceId]

    const nextIds = Object.keys(arraysBySourceId)
    const activeArraySourceId = prev.activeArraySourceId && prev.activeArraySourceId !== removeSourceId && arraysBySourceId[prev.activeArraySourceId]
        ? prev.activeArraySourceId
        : (nextIds.length ? nextIds[nextIds.length - 1] : null)

    return {
        ...state,
        modifierPreviews: buildModifierPreviewsFromMap(arraysBySourceId, activeArraySourceId),
    }
}

const addArrayPreviewSourceFromEntry = (state, entry = {}) => {
    const sourceId = getPreviewSourceIdFromEntry(entry)

    if (!sourceId) return state

    const entryPreviews = normalizeModifierPreviewsState(entry?.modifierPreviews || EMPTY_MODIFIER_PREVIEWS)
    const preview = entry?.historyPayload?.preview
        || entryPreviews.arraysBySourceId?.[sourceId]
        || (entryPreviews.array?.sourceId === sourceId ? entryPreviews.array : null)

    if (!preview?.enabled || !preview?.sourceId) return state

    const prev = normalizeModifierPreviewsState(state.modifierPreviews || EMPTY_MODIFIER_PREVIEWS)
    const arraysBySourceId = {
        ...(prev.arraysBySourceId || {}),
        [preview.sourceId]: normalizeArrayPreviewItem(preview),
    }

    return {
        ...state,
        modifierPreviews: buildModifierPreviewsFromMap(arraysBySourceId, preview.sourceId),
    }
}

const applyArrayPreviewUndoEntry = (state, currentEntry, history) => {
    if (isArrayPreviewSetHistoryEntry(currentEntry)) {
        return {
            ...removeArrayPreviewSourceFromState(state, getPreviewSourceIdFromEntry(currentEntry)),
            history,
        }
    }

    if (isArrayPreviewClearHistoryEntry(currentEntry)) {
        return {
            ...addArrayPreviewSourceFromEntry(state, currentEntry),
            history,
        }
    }

    return null
}

const applyArrayPreviewRedoEntry = (state, nextEntry, history) => {
    if (isArrayPreviewSetHistoryEntry(nextEntry)) {
        return {
            ...addArrayPreviewSourceFromEntry(state, nextEntry),
            history,
        }
    }

    if (isArrayPreviewClearHistoryEntry(nextEntry)) {
        return {
            ...removeArrayPreviewSourceFromState(state, getPreviewSourceIdFromEntry(nextEntry)),
            history,
        }
    }

    return null
}


const safeHydrateObject = (value) => (
    value && typeof value === 'object' && !Array.isArray(value) ? value : {}
)

const safeHydrateArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : [])

const hasHydrateOwn = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key)

const resolveHydratedPersistentSceneSetting = (state, source, name) => {
    const stored = readLocalSceneSetting(name, state[name])

    // Локальная настройка конкретного устройства имеет приоритет над snapshot.
    // Если её ещё нет, первый открытый snapshot становится локальным значением.
    if (stored.found) return stored.value
    if (!hasHydrateOwn(source, name)) return state[name]

    return persistLocalSceneSetting(name, source[name], state[name])
}

const mergeHydrateColors = (snapshotColors = {}) => {
    const incoming = safeHydrateObject(snapshotColors)

    return {
        ...initialState.colors,
        ...incoming,
        instanceByPath: {
            ...(initialState.colors.instanceByPath || {}),
            ...(safeHydrateObject(incoming.instanceByPath)),
        },
        byModelPath: {
            ...(initialState.colors.byModelPath || {}),
            ...(safeHydrateObject(incoming.byModelPath)),
        },
    }
}

const hydrateSceneSnapshotState = (state, snapshot = {}, meta = {}) => {
    const source = safeHydrateObject(snapshot)
    const selectedModels = {
        ...initialState.selectedModels,
        ...safeHydrateObject(source.selectedModels),
    }
    const customObjects = safeHydrateObject(source.customObjects)
    const modelPaths = Array.isArray(source.modelPaths)
        ? safeHydrateArray(source.modelPaths)
        : getDeterministicPaths(selectedModels, customObjects)
    const hasSelectedModel = hasHydrateOwn(source, 'selectedModel')
    const hasMonumentPolished = hasHydrateOwn(source, 'monumentPolished')
    const persistentSceneSettings = LOCAL_SCENE_SETTING_NAMES.reduce((settings, name) => {
        settings[name] = resolveHydratedPersistentSceneSetting(state, source, name)
        return settings
    }, {})

    return {
        ...state,
        selectedModel: hasSelectedModel ? (source.selectedModel || null) : null,
        selectedModelPaths: safeHydrateArray(source.selectedModelPaths),
        selectedModels,
        modelPaths,
        positions: safeHydrateObject(source.positions),
        rotations: safeHydrateObject(source.rotations),
        scales: safeHydrateObject(source.scales),
        pivotPoints: safeHydrateObject(source.pivotPoints),
        pivotPresetById: safeHydrateObject(source.pivotPresetById),
        dimensions: safeHydrateObject(source.dimensions),
        hiddenModels: safeHydrateObject(source.hiddenModels),
        solidObjects: safeHydrateObject(source.solidObjects),
        monumentAssemblies: safeHydrateObject(source.monumentAssemblies),
        monumentDecorations: safeHydrateObject(source.monumentDecorations),
        monumentDecorationAssets: safeHydrateObject(source.monumentDecorationAssets),
        modelPartsMeta: safeHydrateObject(source.modelPartsMeta),
        customObjects,
        colors: mergeHydrateColors(source.colors),
        productsMetaById: {
            ...(state.productsMetaById || {}),
            ...safeHydrateObject(source.productsMetaById),
        },
        selectionGroups: safeHydrateObject(source.selectionGroups),
        activeSelectionGroupId: source.activeSelectionGroupId || null,
        modifierPreviews: normalizeModifierPreviewsState(source.modifierPreviews || EMPTY_MODIFIER_PREVIEWS),
        sceneScreenshots: safeHydrateObject(source.sceneScreenshots),
        measure: safeHydrateObject(source.measure || source.measures),
        cameraState: safeHydrateObject(source.cameraState),
        monumentPolished: hasMonumentPolished ? !!source.monumentPolished : state.monumentPolished,
        showTextDimensions: hasHydrateOwn(source, 'showTextDimensions') ? source.showTextDimensions !== false : state.showTextDimensions,
        lineColor: source.lineColor || state.lineColor,
        lineThickness: source.lineThickness ?? state.lineThickness,
        fontSize: source.fontSize ?? state.fontSize,
        ...persistentSceneSettings,
        minZ: source.minZ ?? state.minZ,
        floorRepeat: source.floorRepeat ?? state.floorRepeat,
        floorSize: source.floorSize ?? state.floorSize,
        floorText: source.floorText || state.floorText,
        history: meta.preserveHistory === true
            ? state.history
            : {
                past: [],
                future: [],
                limit: state.history?.limit || HISTORY_DEFAULT_LIMIT,
                lastHash: '',
            },
        lastHydratedSceneContext: meta.sceneContextKey || meta.contextKey || null,
        lastHydratedAt: Date.now(),
    }
}

export default (state = initialState, action) => {
    switch (action.type) {

        case 'HYDRATE_SCENE_SNAPSHOT': {
            return hydrateSceneSnapshotState(
                state,
                action.payload?.snapshot || {},
                action.payload || {}
            )
        }
        case 'SET_MODEL_PARTS_META': {
            const modelId = String(action.payload?.modelId || '').trim()
            if (!modelId) return state

            const patch = action.payload?.patch && typeof action.payload.patch === 'object'
                ? action.payload.patch
                : {}
            const current = state.modelPartsMeta?.[modelId] && typeof state.modelPartsMeta[modelId] === 'object'
                ? state.modelPartsMeta[modelId]
                : {}
            const nextItem = {
                ...current,
                ...patch,
                namesBySignature: {
                    ...(current.namesBySignature || {}),
                    ...(patch.namesBySignature || {}),
                },
                updatedAt: Date.now(),
            }
            const next = {
                ...(state.modelPartsMeta || {}),
                [modelId]: nextItem,
            }

            return { ...state, modelPartsMeta: next }
        }

        case 'SET_MONUMENT_ASSEMBLIES': {
            const patch = action.payload && typeof action.payload === 'object'
                ? action.payload
                : {}
            const next = { ...(state.monumentAssemblies || {}) }

            Object.keys(patch).forEach((id) => {
                const value = patch[id]
                if (!id) return
                if (!value || value.enabled === false && !value.pedestal?.enabled && !value.foundation?.enabled && !value.flowerbed?.enabled) {
                    delete next[id]
                    return
                }
                next[id] = value
            })

            return { ...state, monumentAssemblies: next }
        }

        case 'SET_MONUMENT_DECORATIONS': {
            const patch = action.payload && typeof action.payload === 'object'
                ? action.payload
                : {}
            const next = { ...(state.monumentDecorations || {}) }

            Object.keys(patch).forEach((id) => {
                const value = patch[id]
                const decorations = Array.isArray(value?.decorations) ? value.decorations.filter(Boolean) : []
                if (!id) return
                if (!value || !decorations.length) {
                    delete next[id]
                    return
                }

                next[id] = {
                    ...value,
                    decorations,
                }
            })

            return { ...state, monumentDecorations: next }
        }

        case 'SET_MONUMENT_DECORATION_ASSETS': {
            const patch = action.payload && typeof action.payload === 'object'
                ? action.payload
                : {}
            return {
                ...state,
                monumentDecorationAssets: {
                    ...(state.monumentDecorationAssets || {}),
                    ...patch,
                },
            }
        }

        case 'SET_SCENE_OBJECTS_SOLID': {
            const ids = Array.isArray(action.payload?.ids) ? action.payload.ids.filter(Boolean) : []
            if (!ids.length) return state

            const nextSolidObjects = { ...(state.solidObjects || {}) }
            ids.forEach((id) => {
                if (action.payload?.solid) nextSolidObjects[id] = true
                else delete nextSolidObjects[id]
            })

            return { ...state, solidObjects: nextSolidObjects }
        }
        case 'PATCH_SCENE_TRANSFORMS': {
            const patch = action.payload && typeof action.payload === 'object'
                ? action.payload
                : {}

            return {
                ...state,
                positions: patch.positions
                    ? { ...state.positions, ...patch.positions }
                    : state.positions,
                rotations: patch.rotations
                    ? { ...state.rotations, ...patch.rotations }
                    : state.rotations,
                scales: patch.scales
                    ? { ...state.scales, ...patch.scales }
                    : state.scales,
            }
        }
        case REPLACE_SCENE_INSTANCE_STATE_TYPE: {
            const patch = action.payload && typeof action.payload === 'object'
                ? action.payload
                : {}

            return {
                ...state,
                ...patch,
                colors: patch.colors || state.colors,
                modifierPreviews: Object.prototype.hasOwnProperty.call(patch, 'modifierPreviews')
                    ? normalizeModifierPreviewsState(patch.modifierPreviews || EMPTY_MODIFIER_PREVIEWS)
                    : state.modifierPreviews,
                history: state.history,
            }
        }
        case 'RESET_SCENE_STATE_TO_DEFAULTS': {
            return {
                ...initialState,
                ...pickPersistentSceneSettings(state),
                history: {
                    past: [],
                    future: [],
                    limit: state.history?.limit || HISTORY_DEFAULT_LIMIT,
                    lastHash: '',
                },
            }
        }
        case sceneConstants.SET_SHOW_PIVOT: return { ...state, showPivot: !!action.payload }
        case sceneConstants.REQUEST_PIVOT_PRESET: return { ...state, pivotRequest: action.payload || null }
        case sceneConstants.CLEAR_PIVOT_REQUEST: return { ...state, pivotRequest: null }
        case sceneConstants.SET_LINE_COLOR: return { ...state, lineColor: action.payload || '#000000' }
        case sceneConstants.SET_PIVOT_PRESET_STATE: {
            const { id, presetKey } = action.payload || {}
            if (!id) return state
            const nextPresetById = { ...(state.pivotPresetById || {}) }
            if (presetKey) nextPresetById[id] = presetKey
            else delete nextPresetById[id]
            return { ...state, pivotPresetById: nextPresetById }
        }
        case sceneConstants.APPLY_PIVOT_PRESET_RESOLVED: {
            const { id, pivot, position, presetKey } = action.payload || {}
            if (!id) return state
            const prevPivot = state.pivotPoints?.[id] || [0, 0, 0]
            const prevPos = state.positions?.[id] || [0, 0, 0]
            const prevPreset = state.pivotPresetById?.[id] || null
            if (prevPreset === presetKey && arr3eq(prevPivot, pivot) && arr3eq(prevPos, position)) {
                return { ...state, pivotRequest: null }
            }
            return {
                ...state, pivotRequest: null,
                pivotPoints: { ...state.pivotPoints, [id]: Array.isArray(pivot) ? pivot : [0, 0, 0] },
                positions: { ...state.positions, [id]: Array.isArray(position) ? position : [0, 0, 0] },
                pivotPresetById: { ...(state.pivotPresetById || {}), [id]: presetKey || null },
            }
        }
        case sceneConstants.SET_SHOW_TEXT_DIMENSIONS: return { ...state, showTextDimensions: !!action.payload }
        case sceneConstants.SCENE_HISTORY_COMMIT: {
            const hist = state.history || { past: [], future: [], limit: HISTORY_DEFAULT_LIMIT, lastHash: '' }
            const rawPast = Array.isArray(hist.past) ? hist.past : []
            const limit = Math.max(1, Number(hist.limit) || HISTORY_DEFAULT_LIMIT)
            const payload = action.payload || state
            const safePayload = Object.prototype.hasOwnProperty.call(payload, 'modifierPreviews')
                ? payload
                : {
                    ...payload,
                    modifierPreviews: state.modifierPreviews || EMPTY_MODIFIER_PREVIEWS,
                }
            const entry = makeHistoryEntry(safePayload, rawPast.length)
            const hash = typeof payload?.historyHash === 'string' && payload.historyHash
                ? payload.historyHash
                : getSnapshotHash(entry)

            const forceCommit = entry.historyKind === 'sceneScreenshotCheckpoint'

            if (!forceCommit && hash === hist.lastHash) return state

            const past = [...rawPast, entry]
            const trimmed = past.length > limit ? past.slice(past.length - limit) : past

            return {
                ...state,
                history: {
                    ...hist,
                    limit,
                    past: trimmed,
                    future: [],
                    lastHash: hash,
                },
            }
        }
        case sceneConstants.SCENE_HISTORY_UNDO: {
            const hist = state.history || { past: [], future: [], limit: HISTORY_DEFAULT_LIMIT, lastHash: '' }
            const past = Array.isArray(hist.past) ? hist.past : []
            const future = Array.isArray(hist.future) ? hist.future : []
            const limit = Math.max(1, Number(hist.limit) || HISTORY_DEFAULT_LIMIT)

            if (past.length <= 1) return state

            const current = past[past.length - 1]
            const prev = past[past.length - 2]
            const nextPast = past.slice(0, past.length - 1)
            const nextFuture = [current, ...future].slice(0, limit)
            const nextHistory = {
                ...hist,
                limit,
                past: nextPast,
                future: nextFuture,
                lastHash: getSnapshotHash(prev),
            }

            const arrayPreviewUndoState = applyArrayPreviewUndoEntry(state, current, nextHistory)
            if (arrayPreviewUndoState) return arrayPreviewUndoState

            return applyHistoryEntry(state, prev, nextHistory)
        }
        case sceneConstants.SCENE_HISTORY_REDO: {
            const hist = state.history || { past: [], future: [], limit: HISTORY_DEFAULT_LIMIT, lastHash: '' }
            const past = Array.isArray(hist.past) ? hist.past : []
            const future = Array.isArray(hist.future) ? hist.future : []
            const limit = Math.max(1, Number(hist.limit) || HISTORY_DEFAULT_LIMIT)

            if (!future.length) return state

            const next = future[0]
            const nextPast = [...past, next].slice(-limit)
            const nextFuture = future.slice(1)
            const nextHistory = {
                ...hist,
                limit,
                past: nextPast,
                future: nextFuture,
                lastHash: getSnapshotHash(next),
            }

            const arrayPreviewRedoState = applyArrayPreviewRedoEntry(state, next, nextHistory)
            if (arrayPreviewRedoState) return arrayPreviewRedoState

            return applyHistoryEntry(state, next, nextHistory)
        }
        case HISTORY_JUMP_TYPE: {
            const hist = state.history || { past: [], future: [], limit: HISTORY_DEFAULT_LIMIT, lastHash: '' }
            const past = Array.isArray(hist.past) ? hist.past : []
            const future = Array.isArray(hist.future) ? hist.future : []
            const timeline = [...past, ...future]
            const limit = Math.max(1, Number(hist.limit) || HISTORY_DEFAULT_LIMIT)

            if (!timeline.length) return state

            const rawIndex = Number(action.payload)
            const index = Number.isFinite(rawIndex)
                ? Math.max(0, Math.min(timeline.length - 1, Math.round(rawIndex)))
                : past.length - 1

            const target = timeline[index]
            const nextPast = timeline.slice(0, index + 1).slice(-limit)
            const nextFuture = timeline.slice(index + 1).slice(0, limit)
            const nextHistory = {
                ...hist,
                limit,
                past: nextPast,
                future: nextFuture,
                lastHash: getSnapshotHash(target),
            }

            const currentIndex = past.length - 1
            const currentEntry = currentIndex >= 0 ? timeline[currentIndex] : null

            if (index === currentIndex - 1 && currentEntry) {
                const arrayPreviewUndoState = applyArrayPreviewUndoEntry(state, currentEntry, nextHistory)
                if (arrayPreviewUndoState) return arrayPreviewUndoState
            }

            if (index === currentIndex + 1 && target) {
                const arrayPreviewRedoState = applyArrayPreviewRedoEntry(state, target, nextHistory)
                if (arrayPreviewRedoState) return arrayPreviewRedoState
            }

            return applyHistoryEntry(state, target, nextHistory)
        }


        case ARRAY_PREVIEW_SET_TYPE: {
            const payload = action.payload || {}
            const normalized = normalizeArrayPreviewItem(payload)
            const prev = normalizeModifierPreviewsState(state.modifierPreviews)

            if (!normalized?.sourceId) {
                return {
                    ...state,
                    modifierPreviews: prev,
                }
            }

            const arraysBySourceId = {
                ...(prev.arraysBySourceId || {}),
                [normalized.sourceId]: normalized,
            }

            const nextState = {
                ...state,
                modifierPreviews: {
                    array: normalized,
                    arraysBySourceId,
                    activeArraySourceId: normalized.sourceId,
                },
            }

            return action.meta?.history
                ? commitHistoryForNextState(nextState, action.meta)
                : nextState
        }

        case ARRAY_PREVIEW_CLEAR_TYPE: {
            const prev = normalizeModifierPreviewsState(state.modifierPreviews)
            const requestedSourceId = action.payload?.sourceId || null
            const removeSourceId = requestedSourceId || prev.activeArraySourceId || Object.keys(prev.arraysBySourceId || {})[0] || null

            if (!removeSourceId) {
                const nextState = {
                    ...state,
                    modifierPreviews: EMPTY_MODIFIER_PREVIEWS,
                }

                return action.meta?.history
                    ? commitHistoryForNextState(nextState, action.meta)
                    : nextState
            }

            const removedPreview = prev.arraysBySourceId?.[removeSourceId] || null
            const arraysBySourceId = { ...(prev.arraysBySourceId || {}) }
            delete arraysBySourceId[removeSourceId]

            const nextIds = Object.keys(arraysBySourceId)
            const activeArraySourceId = prev.activeArraySourceId && prev.activeArraySourceId !== removeSourceId && arraysBySourceId[prev.activeArraySourceId]
                ? prev.activeArraySourceId
                : (nextIds[0] || null)

            const nextState = {
                ...state,
                modifierPreviews: {
                    array: activeArraySourceId ? arraysBySourceId[activeArraySourceId] : null,
                    arraysBySourceId,
                    activeArraySourceId,
                },
            }

            return action.meta?.history
                ? commitHistoryForNextState(nextState, {
                    ...action.meta,
                    historyPayload: {
                        ...(action.meta?.historyPayload || {}),
                        sourceId: removeSourceId,
                        preview: removedPreview,
                    },
                })
                : nextState
        }

        case sceneConstants.SET_FLOOR_VISIBLE: {
            const floorVisible = persistLocalSceneSetting(
                'floorVisible',
                action.payload,
                state.floorVisible
            )

            return floorVisible === state.floorVisible
                ? state
                : { ...state, floorVisible }
        }
        case sceneConstants.SET_FLOOR_MODE: {
            const floorMode = persistLocalSceneSetting(
                'floorMode',
                action.payload,
                state.floorMode
            )

            return floorMode === state.floorMode
                ? state
                : { ...state, floorMode }
        }
        case sceneConstants.SET_FLOOR_COLOR: {
            const floorColor = persistLocalSceneSetting(
                'floorColor',
                action.payload,
                state.floorColor
            )

            return floorColor === state.floorColor
                ? state
                : { ...state, floorColor }
        }
        case sceneConstants.UPSERT_PRODUCTS_META: return { ...state, productsMetaById: { ...(state.productsMetaById || {}), ...(action.payload || {}) } }
        case sceneConstants.SET_TRANSFORM_MODE: return { ...state, transformMode: action.payload || null }
        case sceneConstants.SET_TEXTURE_EDIT_MODE: return { ...state, textureEditMode: !!action.payload }

        case sceneConstants.SET_COLORS:
        case 'SET_COLORS': {
            const incoming = action.payload || {};
            return {
                ...state,
                colors: {
                    ...state.colors,
                    ...incoming,
                    instanceByPath: { ...(state.colors.instanceByPath || {}), ...(incoming.instanceByPath || {}) },
                    byModelPath: { ...(state.colors.byModelPath || {}), ...(incoming.byModelPath || {}) }
                }
            };
        }

        case sceneConstants.SET_CUSTOM_OBJECTS: {
            const incomingRaw = action.payload && typeof action.payload === 'object' ? action.payload : {}
            const prev = state.customObjects || {}
            const prevIds = new Set(Object.keys(prev))
            const nextIds = new Set(Object.keys(incomingRaw))
            const removed = []
            prevIds.forEach((id) => { if (!nextIds.has(id)) removed.push(id) })
            const incoming = {}
            Object.keys(incomingRaw).forEach((id) => {
                const obj = incomingRaw[id]
                const sc = state.scales?.[id] || [1, 1, 1]
                incoming[id] = applyAutoRepeatToCustomObject(obj, sc)
            })
            const nextHidden = { ...(state.hiddenModels || {}) }
            const nextPositions = { ...(state.positions || {}) }
            const nextRotations = { ...(state.rotations || {}) }
            const nextScales = { ...(state.scales || {}) }
            const nextPivot = { ...(state.pivotPoints || {}) }
            const nextPresetById = { ...(state.pivotPresetById || {}) }
            const nextDims = { ...(state.dimensions || {}) }
            const nextSolidObjects = { ...(state.solidObjects || {}) }
            removed.forEach((id) => {
                delete nextHidden[id]; delete nextPositions[id]; delete nextRotations[id]
                delete nextScales[id]; delete nextPivot[id]; delete nextPresetById[id]; delete nextDims[id]
                delete nextSolidObjects[id]
            })
            const selectedWasRemoved = state.selectedModel && removed.includes(state.selectedModel)
            const nextSelectedModel = selectedWasRemoved ? null : state.selectedModel
            const nextSelectedPaths = (state.selectedModelPaths || []).filter((p) => !removed.includes(p))
            const nextModelPaths = getDeterministicPaths(state.selectedModels, incoming)
            return {
                ...state, customObjects: incoming, hiddenModels: nextHidden, positions: nextPositions,
                rotations: nextRotations, scales: nextScales, pivotPoints: nextPivot,
                pivotPresetById: nextPresetById, dimensions: nextDims, solidObjects: nextSolidObjects, selectedModel: nextSelectedModel,
                selectedModelPaths: nextSelectedModel ? nextSelectedPaths : [], modelPaths: nextModelPaths,
            }
        }
        case sceneConstants.CUSTOM_OBJECT_CREATE: {
            const objRaw = action.payload
            const id = objRaw?.id
            if (!id) return state
            const obj = applyAutoRepeatToCustomObject(objRaw, [1, 1, 1])
            const nextCustom = { ...(state.customObjects || {}), [id]: obj }
            const d = ensureDefaultsForId(state, id, obj?.baseDimensions || null)
            const mp = new Set(Array.isArray(state.modelPaths) ? state.modelPaths : [])
            mp.add(id)
            return {
                ...state, customObjects: nextCustom, positions: d.nextPositions, rotations: d.nextRotations,
                scales: d.nextScales, dimensions: d.nextDimensions, modelPaths: Array.from(mp),
            }
        }
        case sceneConstants.CUSTOM_OBJECT_UPDATE: {
            const id = action.payload?.id
            const patch = action.payload?.patch
            if (!id || !patch) return state
            const prev = state.customObjects?.[id]
            if (!prev) return state
            let nextObj = { ...prev, ...patch }
            if (patch.textData && typeof patch.textData === 'object') {
                nextObj.textData = { ...(prev.textData || {}), ...(patch.textData || {}) }
            }
            if (patch.baseDimensions && typeof patch.baseDimensions === 'object') {
                nextObj.baseDimensions = { ...(prev.baseDimensions || {}), ...(patch.baseDimensions || {}) }
            }
            if (patch.texture && typeof patch.texture === 'object') {
                nextObj.texture = { ...(prev.texture || {}), ...(patch.texture || {}) }
            }
            const sc = state.scales?.[id] || [1, 1, 1]
            nextObj = applyAutoRepeatToCustomObject(nextObj, sc)
            const nextCustom = { ...(state.customObjects || {}), [id]: nextObj }
            const d = ensureDefaultsForId(state, id, nextObj?.baseDimensions || null)
            const mp = new Set(Array.isArray(state.modelPaths) ? state.modelPaths : [])
            mp.add(id)
            return {
                ...state, customObjects: nextCustom, positions: d.nextPositions, rotations: d.nextRotations,
                scales: d.nextScales, dimensions: d.nextDimensions, modelPaths: Array.from(mp),
            }
        }
        case sceneConstants.CUSTOM_OBJECT_DELETE: {
            const id = action.payload
            if (!id) return state
            if (!state.customObjects?.[id]) return state
            const nextCustom = { ...(state.customObjects || {}) }
            delete nextCustom[id]
            const nextHidden = { ...(state.hiddenModels || {}) }
            const nextPositions = { ...(state.positions || {}) }
            const nextRotations = { ...(state.rotations || {}) }
            const nextScales = { ...(state.scales || {}) }
            const nextPivot = { ...(state.pivotPoints || {}) }
            const nextPresetById = { ...(state.pivotPresetById || {}) }
            const nextDims = { ...(state.dimensions || {}) }
            const nextSolidObjects = { ...(state.solidObjects || {}) }
            delete nextHidden[id]; delete nextPositions[id]; delete nextRotations[id]
            delete nextScales[id]; delete nextPivot[id]; delete nextPresetById[id]; delete nextDims[id]
            delete nextSolidObjects[id]
            const mp = new Set(Array.isArray(state.modelPaths) ? state.modelPaths : [])
            mp.delete(id)
            const nextSelectedPaths = (state.selectedModelPaths || []).filter((p) => p !== id)
            const nextSelectedModel = state.selectedModel === id ? (nextSelectedPaths[nextSelectedPaths.length - 1] || null) : state.selectedModel
            return {
                ...state, customObjects: nextCustom, hiddenModels: nextHidden, positions: nextPositions,
                rotations: nextRotations, scales: nextScales, pivotPoints: nextPivot,
                pivotPresetById: nextPresetById, dimensions: nextDims, solidObjects: nextSolidObjects, modelPaths: Array.from(mp),
                selectedModel: nextSelectedModel, selectedModelPaths: nextSelectedModel ? nextSelectedPaths : [],
            }
        }
        case sceneConstants.TOGGLE_HIDDEN_MODEL: {
            const path = action.payload
            if (!path) return state
            const nextHidden = { ...(state.hiddenModels || {}) }
            if (nextHidden[path]) delete nextHidden[path]
            else nextHidden[path] = true
            const nextSelected = state.selectedModel === path && nextHidden[path] ? null : state.selectedModel
            const nextSelectedPaths = (state.selectedModelPaths || []).filter((p) => !nextHidden[p])
            return { ...state, hiddenModels: nextHidden, selectedModel: nextSelected, selectedModelPaths: nextSelected ? nextSelectedPaths : [] }
        }
        case sceneConstants.SET_MOVE_MODE: return { ...state, moveMode: action.payload }
        case sceneConstants.SET_MOVE_STEP: return { ...state, moveStep: action.payload }
        case sceneConstants.SET_CTRL_PRESSED: return { ...state, isCtrlPressed: action.payload }
        case sceneConstants.SET_MONUMENT_POLISHED: return { ...state, monumentPolished: action.payload }
        case sceneConstants.SET_SELECTED_MODEL: {
            const nextSelectedModel = action.payload
            const prevModifierPreviews = normalizeModifierPreviewsState(state.modifierPreviews)
            const selectedPreview = nextSelectedModel
                ? prevModifierPreviews.arraysBySourceId?.[nextSelectedModel] || null
                : null

            return {
                ...state,
                selectedModel: nextSelectedModel,
                modifierPreviews: selectedPreview
                    ? {
                        array: selectedPreview,
                        arraysBySourceId: prevModifierPreviews.arraysBySourceId,
                        activeArraySourceId: nextSelectedModel,
                    }
                    : prevModifierPreviews,
            }
        }
        case sceneConstants.SET_SELECTED_MODEL_PATHS: return { ...state, selectedModelPaths: Array.isArray(action.payload) ? action.payload : [] }
        case sceneConstants.TOGGLE_MULTI_SELECT_MODE: return { ...state, multiSelectMode: !state.multiSelectMode }
        case sceneConstants.UPSERT_SELECTION_GROUP: {
            const g = action.payload
            if (!g || !g.id) return state
            return { ...state, selectionGroups: { ...(state.selectionGroups || {}), [g.id]: g } }
        }
        case sceneConstants.SET_ACTIVE_SELECTION_GROUP: return { ...state, activeSelectionGroupId: action.payload || null }

        case sceneConstants.SET_POSITIONS: {
            const patch = action.payload || {}
            const nextPresetById = { ...(state.pivotPresetById || {}) }
            Object.keys(patch).forEach((id) => { delete nextPresetById[id] })
            return { ...state, positions: { ...state.positions, ...patch }, pivotPresetById: nextPresetById }
        }
        case sceneConstants.SET_ROTATIONS: {
            const patch = action.payload || {}
            const nextPresetById = { ...(state.pivotPresetById || {}) }
            Object.keys(patch).forEach((id) => { delete nextPresetById[id] })
            return { ...state, rotations: { ...state.rotations, ...patch }, pivotPresetById: nextPresetById }
        }
        case sceneConstants.SET_SCALES: {
            const incoming = action.payload || {}
            const nextScales = { ...state.scales, ...incoming }
            const nextPresetById = { ...(state.pivotPresetById || {}) }
            Object.keys(incoming).forEach((id) => { delete nextPresetById[id] })
            let nextCustomObjects = state.customObjects
            let changed = false
            Object.keys(incoming).forEach((id) => {
                const obj = state.customObjects?.[id]
                if (!obj?.texture?.lockToSize) return
                const fixed = applyAutoRepeatToCustomObject(obj, nextScales[id] || [1, 1, 1])
                if (!changed) {
                    nextCustomObjects = { ...(state.customObjects || {}) }
                    changed = true
                }
                nextCustomObjects[id] = fixed
            })
            return { ...state, scales: nextScales, customObjects: changed ? nextCustomObjects : state.customObjects, pivotPresetById: nextPresetById }
        }
        case sceneConstants.SET_PIVOT_POINT: {
            const patch = action.payload || {}
            const nextPresetById = { ...(state.pivotPresetById || {}) }
            Object.keys(patch).forEach((id) => { delete nextPresetById[id] })
            return { ...state, pivotPoints: { ...state.pivotPoints, ...patch }, pivotPresetById: nextPresetById }
        }
        case sceneConstants.SET_DIMENSIONS: return { ...state, dimensions: { ...state.dimensions, ...(action.payload || {}) } }
        case sceneConstants.SET_MODEL_PATHS: return { ...state, modelPaths: action.payload }

        // ИСПРАВЛЕНО: Безупречная миграция сдвигаемых копий
        case sceneConstants.SET_SELECTED_MODELS: {
            const incoming = action.payload || {};
            const nextRawSelected = { ...state.selectedModels, ...incoming };
            const cleanSelectedModels = {};

            const processBaseId = (rawId) => rawId ? getBaseId(rawId) : rawId;

            if (nextRawSelected.karkas) cleanSelectedModels.karkas = processBaseId(nextRawSelected.karkas);
            if (nextRawSelected.monumentLeft) cleanSelectedModels.monumentLeft = processBaseId(nextRawSelected.monumentLeft);

            const keys = Object.keys(nextRawSelected).filter(k => k !== 'karkas' && k !== 'monumentLeft' && k !== 'gravestones').sort();
            keys.forEach(k => {
                const val = nextRawSelected[k];
                if (Array.isArray(val)) cleanSelectedModels[k] = val.map(processBaseId);
                else if (typeof val === 'string' && val) cleanSelectedModels[k] = processBaseId(val);
                else cleanSelectedModels[k] = val;
            });

            const buildPathMap = (selModels) => {
                const map = {};
                const counts = {};
                const add = (id, cat, idx) => {
                    if (!id) return;
                    const bId = getBaseId(id);
                    const path = counts[bId] ? `${bId}__${counts[bId]}` : bId;
                    counts[bId] = (counts[bId] || 0) + 1;
                    if (cat) {
                        if (!map[cat]) map[cat] = {};
                        map[cat][idx] = path;
                    }
                };
                if (selModels.karkas) add(selModels.karkas, 'karkas', 0);
                if (selModels.monumentLeft) add(selModels.monumentLeft, 'monumentLeft', 0);
                keys.forEach(k => {
                    const v = selModels[k];
                    if (Array.isArray(v)) v.forEach((id, i) => add(id, k, i));
                    else if (typeof v === 'string') add(v, k, 0);
                });
                return map;
            };

            const oldPathMap = buildPathMap(state.selectedModels);
            const newPathMap = buildPathMap(cleanSelectedModels);

            const oldToNew = {};
            Object.keys(newPathMap).forEach(catKey => {
                Object.keys(newPathMap[catKey]).forEach(idx => {
                    const newPath = newPathMap[catKey][idx];
                    const oldPath = oldPathMap[catKey]?.[idx];
                    if (oldPath && newPath && oldPath !== newPath && getBaseId(oldPath) === getBaseId(newPath)) {
                        oldToNew[oldPath] = newPath;
                    }
                });
            });

            const migrateDict = (dict) => {
                const finalDict = { ...dict };
                Object.keys(oldToNew).forEach(oldId => {
                    const newId = oldToNew[oldId];
                    if (dict[oldId] !== undefined) {
                        finalDict[newId] = dict[oldId];
                    }
                });
                Object.keys(oldToNew).forEach(oldId => {
                    if (!Object.values(oldToNew).includes(oldId)) {
                        delete finalDict[oldId];
                    }
                });
                return finalDict;
            };

            const nextPositions = migrateDict(state.positions || {});
            const nextRotations = migrateDict(state.rotations || {});
            const nextScales = migrateDict(state.scales || {});
            const nextPivotPoints = migrateDict(state.pivotPoints || {});
            const nextDimensions = migrateDict(state.dimensions || {});
            const nextInstanceByPath = migrateDict(state.colors.instanceByPath || {});
            const migratedSolidObjects = migrateDict(state.solidObjects || {});
            const migratedMonumentAssemblies = migrateDict(state.monumentAssemblies || {});
            const migratedMonumentDecorations = migrateDict(state.monumentDecorations || {});

            let newColors = { ...state.colors, instanceByPath: nextInstanceByPath };
            if (incoming.gravestones) {
                newColors.gravestones = incoming.gravestones.map((_, index) => state.colors.gravestones[index] || { material: 'Габро-карельский', polish: '1 сторона' });
            }

            const newModelPaths = getDeterministicPaths(cleanSelectedModels, state.customObjects);
            const validModelPathSet = new Set(newModelPaths);
            const nextSolidObjects = Object.keys(migratedSolidObjects).reduce((acc, id) => {
                if (validModelPathSet.has(id) && migratedSolidObjects[id] === true) acc[id] = true;
                return acc;
            }, {});
            const nextMonumentAssemblies = Object.keys(migratedMonumentAssemblies).reduce((acc, id) => {
                if (validModelPathSet.has(id) && migratedMonumentAssemblies[id]) acc[id] = migratedMonumentAssemblies[id]
                return acc
            }, {})
            const nextMonumentDecorations = Object.keys(migratedMonumentDecorations).reduce((acc, id) => {
                if (validModelPathSet.has(id) && migratedMonumentDecorations[id]) acc[id] = migratedMonumentDecorations[id]
                return acc
            }, {})
            const nextPositionsWithDefaults = { ...nextPositions };

            Object.keys(newPathMap).forEach(catKey => {
                const rows = Object.keys(newPathMap[catKey] || {})
                    .map(idx => ({ idx: Number(idx), path: newPathMap[catKey][idx] }))
                    .filter(item => item.path)
                    .sort((a, b) => a.idx - b.idx);

                if (rows.length <= 1) return;

                rows.forEach(({ idx, path }) => {
                    if (nextPositionsWithDefaults[path] !== undefined) return;

                    const baseId = getBaseId(path);
                    const baseDim = nextDimensions[path] || nextDimensions[baseId] || state.dimensions?.[path] || state.dimensions?.[baseId] || {};
                    const rawStep = Number(baseDim.x) || 1;
                    const step = Math.max(0.75, Math.min(3, rawStep + 0.35));

                    nextPositionsWithDefaults[path] = [
                        step * idx,
                        0,
                        0,
                    ];
                });
            });

            return {
                ...state,
                selectedModels: cleanSelectedModels,
                colors: newColors,
                modelPaths: newModelPaths,
                positions: nextPositionsWithDefaults,
                rotations: nextRotations,
                scales: nextScales,
                pivotPoints: nextPivotPoints,
                dimensions: nextDimensions,
                solidObjects: nextSolidObjects,
                monumentAssemblies: nextMonumentAssemblies,
                monumentDecorations: nextMonumentDecorations
            };
        }

        case sceneConstants.SET_FENCE_COLOR: return { ...state, colors: { ...state.colors, fence: action.payload } }
        case sceneConstants.SET_SCENE_COLOR: {
            const sceneColor = persistLocalSceneSetting(
                'sceneColor',
                action.payload,
                state.sceneColor
            )

            return sceneColor === state.sceneColor
                ? state
                : { ...state, sceneColor }
        }
        case sceneConstants.SET_GRID_VISIBLE: {
            const gridVisible = persistLocalSceneSetting(
                'gridVisible',
                action.payload,
                state.gridVisible
            )

            return gridVisible === state.gridVisible
                ? state
                : { ...state, gridVisible }
        }
        case sceneConstants.SET_GRID_COLOR: {
            const gridColor = persistLocalSceneSetting(
                'gridColor',
                action.payload,
                state.gridColor
            )

            return gridColor === state.gridColor
                ? state
                : { ...state, gridColor }
        }
        case sceneConstants.SET_GRID_POSITION: return { ...state, gridPosition: action.payload }
        case sceneConstants.SET_MIN_Z: return { ...state, minZ: action.payload }
        case sceneConstants.SET_PROCESSED_PHOTO: return { ...state, processedPhoto: action.payload }
        case sceneConstants.SET_HEAD_CENTER: return { ...state, headCenter: action.payload }
        case sceneConstants.SET_SHOW_DIMENSION_LINES: return { ...state, showDimensionLines: action.payload }
        case sceneConstants.SET_LINE_THICKNESS: return { ...state, lineThickness: action.payload }
        case sceneConstants.SET_FONT_SIZE: return { ...state, fontSize: action.payload }
        case sceneConstants.SET_CAMERA_STATE: return { ...state, cameraState: { ...state.cameraState, ...action.payload } }
        case sceneConstants.SET_SUN_ANGLE: {
            const sunAngle = persistLocalSceneSetting(
                'sunAngle',
                action.payload,
                state.sunAngle
            )

            return sunAngle === state.sunAngle
                ? state
                : { ...state, sunAngle }
        }
        case sceneConstants.SET_SUN_VERTICAL: {
            const sunVertical = persistLocalSceneSetting(
                'sunVertical',
                action.payload,
                state.sunVertical
            )

            return sunVertical === state.sunVertical
                ? state
                : { ...state, sunVertical }
        }
        case sceneConstants.SET_SUN_INTENSITY: {
            const sunIntensity = persistLocalSceneSetting(
                'sunIntensity',
                action.payload,
                state.sunIntensity
            )

            return sunIntensity === state.sunIntensity
                ? state
                : { ...state, sunIntensity }
        }

        case sceneConstants.SET_SHADOWS_ENABLED: {
            const shadowsEnabled = persistLocalSceneSetting(
                'shadowsEnabled',
                action.payload,
                state.shadowsEnabled
            )

            return shadowsEnabled === state.shadowsEnabled
                ? state
                : { ...state, shadowsEnabled }
        }

        case sceneConstants.SET_GRAPHICS_QUALITY: {
            const graphicsQuality = persistLocalSceneSetting(
                'graphicsQuality',
                action.payload,
                state.graphicsQuality
            )

            return graphicsQuality === state.graphicsQuality
                ? state
                : { ...state, graphicsQuality }
        }

        default: return state
    }
}
